import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';

import { AppDispatch } from '../../../redux/reducer/store';
import { actionTableEmployeesList } from '../../../redux/action/accessRights/actionUsersList';

import { reducerSelectedUserId, reducerUsersInputValues, reducerClearFilters } from '../../../redux/reducer/accessRights/reducers/usersList';

import { formatReports } from '../../../utils/formatReports';
import Loader from '../../custom/loader/loader';
import Select from '../../custom/select/select';
import Pagination from '../../pagination/pagination';
import LimitSelect from '../../custom/limitSelect/limitSelect';

import styles from '../headerBtn.module.scss';
import '../../table/table.scss';
import { IUserOptions } from '../../../types/interfaces';

interface IEmployeeOptions {
  id: number;
  familyName?: string;
  givenName?: string;
  middleName?: string;
  login: string;
  role: { [key: string]: string };
  position?: string;
};

const NewUsersClaims: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    userInputValues,
    usersFilters,
    checkboxes,
    usersCount,
    limit,
    pages,
    data,
  } = useSelector((state: any) => state.roles.usersList);

  const {
    selectedEmployeesId,
    //clientsFilters
  } = useSelector((state: any) => state.roles.customerList);

  const { claims } = useSelector((state: IUserOptions) => state.user.user.data);
  const [isCheckedRows, setIsCheckedRows] = useState<boolean[]>([]);
  const [isInputChange, setIsInputChange] = useState<boolean>(false);
  const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [startPage, setStartPage] = useState<number>(1);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [isVisibleDataList, setIsVisibleDataList] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string[]>([]);

  const actualLimit =
    JSON.parse(localStorage.getItem('pageLimit') as string) || limit;

  const checkboxList = (check: boolean) => {
    const updatedCheckedRows =
      data &&
      data.reduce((acc: { [key: number]: boolean }, user: any) => {
        const userId = user?.id;
        acc[userId] = check;
        return acc;
      }, {});
    dispatch(reducerSelectedUserId(formatReports(updatedCheckedRows)));
    setIsCheckedRows(updatedCheckedRows);
  };

  useEffect(() => {
    if (selectedEmployeesId && !selectedEmployeesId.length) {
      setIsCheckedRows([]);
      setIsHeaderCheckboxChecked(false);
    }
  }, [checkboxes, selectedEmployeesId]);

  useEffect(() => {
    if (data) {
      checkboxList(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(reducerUsersInputValues({ ...userInputValues }))
    dispatch(actionTableEmployeesList(limit, pages, false));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isHeaderCheckboxChecked && data) {
      checkboxList(isHeaderCheckboxChecked);
    }
    // eslint-disable-next-line
  }, [dispatch, isHeaderCheckboxChecked, data]);

  const handleHeaderCheckboxChange = () => {
    setIsHeaderCheckboxChecked(!isHeaderCheckboxChecked);
    if (isHeaderCheckboxChecked) {
      checkboxList(!isHeaderCheckboxChecked);
    }
  };

  useEffect(() => {
    const handleDocumentClick = (event: React.ChangeEvent<HTMLInputElement> | Event): void => {
      const inputEvent = event as React.ChangeEvent<HTMLInputElement>;
      if (inputEvent.target.name !== 'login') {
        setIsVisibleDataList(false);
        setIsPending(true);
        dispatch(actionTableEmployeesList(limit, pages, false)).then(() => {
          setIsPending(false);
        });
        setIsInputChange(false);
        resetPages();
      }
    };

    if (isInputChange) {
      document.addEventListener('click', handleDocumentClick);
      return () => {
        document.removeEventListener('click', handleDocumentClick);
      };
    } else {
      document.removeEventListener('click', handleDocumentClick);
    }
  }, [dispatch, isInputChange, limit, pages]);

  const handleRowCheckboxChange = (el: number) => {
    const employeeId = el;
    setIsCheckedRows((prevCheckedRows: boolean[]) => ({
      ...prevCheckedRows,
      [employeeId]: !prevCheckedRows[employeeId],
    }));
    const updatedSelectedVacancies = formatReports({
      ...isCheckedRows,
      [employeeId]: !isCheckedRows[employeeId],
    });
    dispatch(reducerSelectedUserId(updatedSelectedVacancies));
    //dispatch(reducerSelectedVacancies(el));
    setIsHeaderCheckboxChecked(updatedSelectedVacancies.length === data.length);
  };

  const selectChange = (event: { value: string, label: string }, name: string) => {
    const value = event.value;
    if (value === '') {
      const updatedInputValues = { ...userInputValues };
      delete updatedInputValues[name];
      dispatch(reducerUsersInputValues(updatedInputValues));
    } else {
      dispatch(
        reducerUsersInputValues({
          ...userInputValues,
          [name]: value,
        })
      )
    }
    setIsPending(true);
    dispatch(actionTableEmployeesList(limit, pages, false)).then(() => {
      setIsPending(false);
    });
    setIsInputChange(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value === '') {
      const updatedInputValues = { ...userInputValues };
      delete updatedInputValues[name];
      dispatch(reducerUsersInputValues(updatedInputValues));
      setSearchTerm([]);
      setIsVisibleDataList(false);
      setIsPending(true);
      dispatch(actionTableEmployeesList(limit, pages, false)).then(() => {
        setIsPending(false);
      })
    } else {
      if (name === 'login') {
        setIsVisibleDataList(true);
        const filteredOptions = usersFilters?.logins?.filter((option: string) =>
          option?.toLowerCase()?.includes(value?.toLowerCase()));
        setSearchTerm(filteredOptions);
      }
      dispatch(
        reducerUsersInputValues({
          ...userInputValues,
          [name]: value
        })
      );
    }
    setIsInputChange(true);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setIsPending(true);
      dispatch(actionTableEmployeesList(actualLimit, pages, false)).then(() => {
        setIsPending(false);
      });
      setIsInputChange(false);
      resetPages();
    }
  };

  const handleChange = (newLimit: number, newPage: number) => {
    setIsPending(true);
    dispatch(actionTableEmployeesList(newLimit, newPage, false)).then(() => {
      setIsPending(false);
    });
    localStorage.setItem('pageLimit', JSON.stringify(newLimit));
  };

  const handleClickDataList = (item: string) => {
    dispatch(
      reducerUsersInputValues({
        ...userInputValues,
        login: item,
      })
    );
    setIsInputChange(true);
    setIsPending(true);
    dispatch(actionTableEmployeesList(limit, pages, false)).then(() => {
      setIsPending(false);
      setIsVisibleDataList(false);
    })
  };

  const resetPages = () => {
    setCurrentPage(1);
    setStartPage(1);
  };

  const handleChangeLimits = (option: { value: number }) => {
    handleChange(option.value, 1);
    resetPages();
  };

  useEffect(() => {
    dispatch(reducerClearFilters());
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <div className='loader__wrapper'>
        {(isPending) && <Loader />}
      </div>
      <div className='table'>
        <div className='table__wrapper table__wrapper-application'>
          <table>
            <thead className='table__header'>
              <tr>
                <th className='reports__checkbox_block' style={{ width: '40px' }}>
                  {(claims?.includes('admin') || claims?.includes('claim:employee:add')) &&
                    <input
                      onChange={handleHeaderCheckboxChange}
                      checked={isHeaderCheckboxChecked}
                      type='checkbox'
                    />}
                </th>

                <th style={{ minWidth: '280px' }}>
                  <div className='filter__block'>
                    <input
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      autoComplete='off'
                      placeholder=' '
                      name='familyName'
                      type='text'
                    />
                    <label>ФИО</label>
                  </div>
                </th>

                <th style={{ width: '300px' }}>
                  <div className='filter__number_block'>
                    <input
                      value={userInputValues?.login || ''}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=' '
                      name='login'
                      type='text'
                      autoComplete={'off'}
                    />
                    <label>Логин</label>
                  </div>
                  {isVisibleDataList && searchTerm.length > 0 && <ul className='dataList'>
                    {(usersFilters && usersFilters?.logins) && searchTerm.map((item: string, index: number) => (
                      <li className='dataItem' key={index} onClick={() => { handleClickDataList(item) }}>{item}</li>
                    ))}
                  </ul>}
                </th>

                <th>
                  <div className={clsx('filter__number_block')}>
                    <Select
                      options={usersFilters?.roles}
                      propsChange={(event: { value: string, label: string }) =>
                        selectChange(event, "role")
                      }
                      placeholder={'Роль'}
                      className='users__filter'
                      name="role"
                      span='Роль'
                      isShowPlaceholder={userInputValues && userInputValues?.role}
                    />
                  </div>
                </th>

              </tr>
            </thead>
            <tbody>
              {data && data.map((el: IEmployeeOptions, index: number) => {
                const name = [el?.familyName, el?.givenName, el?.middleName].join(' ');
                return (
                  <tr className='active__link_tr' key={index}>
                    <td>
                      {(claims?.includes('admin') || claims?.includes('claim:employee:add')) &&
                        <input
                          onChange={() => handleRowCheckboxChange(el.id)}
                          className='relative__elem'
                          type='checkbox'
                          checked={
                            (isCheckedRows && isCheckedRows[el.id]) || false
                          }
                        />}
                    </td>
                    <td>
                      <span className={styles.relative__elem}>
                        {name}
                      </span>
                    </td>

                    <td>
                      <span className={styles.relative__elem}>
                        {el?.login}
                      </span>
                    </td>

                    <td>
                      <span className={styles.relative__elem}>
                        {Object.values(el?.role).toString()}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className='pagination'>
          <div className='pagination__count'>Всего: {usersCount}</div>
          <div className='pagination__str'>
            <span>Отображать строки</span>
            <LimitSelect limit={actualLimit} propsChange={handleChangeLimits} />
          </div>
          <Pagination
            setCurrentPage={setCurrentPage}
            setStartPage={setStartPage}
            handleChange={handleChange}
            count={usersCount}
            currentPage={currentPage}
            startPage={startPage}
            limit={actualLimit}
            pageName='userReports'
          />
        </div>
      </div>
    </div>
  );
};

export default NewUsersClaims;
